import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import api from '../api/api';
import { CURRENCY } from '../config/currency';
import { DisplayContext, formatOrderId } from '../utils/orderFormatter';
import MenuItemImage from '../components/MenuItemImage';
import { useTheme } from '../context/ThemeContext';
// Toaster provided globally via ToastProvider

interface OrderTrackingInfo {
  orderId: number;
  customOrderId?: string;
  status: string;
  createdAt: string;
  updatedAt: string;
  total: number;
  deliveryType: string;
  customerName: string;
  customerPhone: string;
  statusTimestamps: {
    acceptedAt?: string;
    preparingAt?: string;
    readyAt?: string;
    completedAt?: string;
  };
  items: Array<{
    name: string;
    quantity: number;
    price: number;
  }>;
  vendorName: string;
}

const GuestOrderTracking: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { theme } = useTheme();
  
  const [trackingToken, setTrackingToken] = useState(searchParams.get('token') || '');
  const [phoneNumber, setPhoneNumber] = useState(searchParams.get('phone') || '');
  const [orderInfo, setOrderInfo] = useState<OrderTrackingInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Track order function
  const handleTrackOrder = useCallback(async () => {
    if (!trackingToken.trim()) {
      setError('Please enter a tracking token');
      return;
    }
    
    if (!phoneNumber.trim()) {
      setError('Please enter your phone number');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await api.get('/auth/guest/track-order', {
        params: {
          token: trackingToken,
          phone: phoneNumber
        }
      });
      
      setOrderInfo(response.data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to track order. Please check your tracking information.');
    } finally {
      setLoading(false);
    }
  }, [trackingToken, phoneNumber]);

  // Auto-track if URL params are provided
  useEffect(() => {
    if (trackingToken && phoneNumber) {
      handleTrackOrder();
    }
  }, [trackingToken, phoneNumber, handleTrackOrder]);


  const getStatusColor = (status: string) => {
    const statusTheme = theme.components?.status;
    if (statusTheme) {
      const key = status.toLowerCase();
      if (statusTheme[key]) return statusTheme[key].text;
    }
    // Fallback colours — Tier-2 status tokens, not raw hex. The ladder reads
    // as: waiting on someone (warn) -> in motion (info) -> done (ok) -> stopped
    // (bad). PENDING and PREPARING were the same amber before and stay
    // deliberately identical: both mean "the kitchen has it, wait".
    switch (status) {
      case 'PENDING':
      case 'PREPARING':
        return 'var(--status-warn)';
      case 'ACCEPTED':
        return 'var(--status-info)';
      case 'READY':
        return 'var(--status-ok)';
      case 'COMPLETED':
        // Was a darker emerald than READY — keep that distinction: a finished
        // order reads settled, not still-active.
        return 'var(--status-ok-text)';
      case 'CANCELLED':
        return 'var(--status-bad)';
      default:
        return 'var(--text-tertiary)';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'PENDING':
        return '⏳';
      case 'ACCEPTED':
        return '✅';
      case 'PREPARING':
        return '👨‍🍳';
      case 'READY':
        return '🍽️';
      case 'COMPLETED':
        return '🎉';
      case 'CANCELLED':
        return '❌';
      default:
        return '📋';
    }
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  // Symbol follows the vendor-scoped currency set when the guest scanned the QR
  // (falls back to the login/default currency) — see src/config/currency.ts
  const formatCurrency = (amount: number) => {
    return `${CURRENCY.symbol}${amount.toFixed(2)}`;
  };

  return (
    <>
      <div className="min-h-screen bg-[var(--color-background-default)]">
      {/* Header */}
      <div className="bg-[var(--color-background-surface)] shadow-sm border-b border-[var(--color-border-default)]">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate('/')}
              className="p-2 -ml-2 text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <h1 className="text-xl font-semibold text-[var(--color-text-primary)]" style={{ fontFamily: 'var(--font-family-heading)' }}>
              Track Your Order
            </h1>
            <div className="w-9"></div>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-8">
        {!orderInfo ? (
          /* Tracking Form */
          <div className="p-6 shadow-sm" style={{
            backgroundColor: theme.components.card.background,
            borderRadius: '16px',
            border: `1px solid ${theme.components.card.border}`,
          }}>
            <div className="text-center mb-6">
              <div className="text-4xl mb-4">📦</div>
              <h2 className="text-2xl font-bold text-[var(--color-text-primary)] mb-2">
                Track Your Order
              </h2>
              <p className="text-[var(--color-text-secondary)]">
                Enter your tracking information to see your order status
              </p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[var(--color-text-primary)] mb-2">
                  Tracking Token
                </label>
                <input
                  type="text"
                  value={trackingToken}
                  onChange={(e) => setTrackingToken(e.target.value)}
                  placeholder="GT_XXXXXXXXXXXXXXXX"
                  aria-label="Order tracking token"
                  className="w-full px-4 py-3 bg-[var(--color-background-default)] border border-[var(--color-border-default)] rounded-lg text-[var(--color-text-primary)] placeholder-[var(--color-text-secondary)] focus:ring-2 focus:ring-[color:var(--focus-ring)] focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[var(--color-text-primary)] mb-2">
                  Phone Number
                </label>
                <input
                  type="tel"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  placeholder="Enter your phone number"
                  className="w-full px-4 py-3 bg-[var(--color-background-default)] border border-[var(--color-border-default)] rounded-lg text-[var(--color-text-primary)] placeholder-[var(--color-text-secondary)] focus:ring-2 focus:ring-[color:var(--focus-ring)] focus:border-transparent"
                />
              </div>

              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg" role="alert">
                  <p className="text-red-800 text-sm">{error}</p>
                </div>
              )}

              <button
                onClick={handleTrackOrder}
                disabled={loading || !trackingToken.trim() || !phoneNumber.trim()}
                className="w-full py-3 px-6 hover:opacity-90 font-semibold rounded-xl transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                  backgroundColor: theme.button.primary.solidBg || theme.button.primary.bg,
                  color: theme.button.primary.text,
                }}
              >
                {loading ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    Tracking...
                  </div>
                ) : (
                  'Track Order'
                )}
              </button>
            </div>
          </div>
        ) : (
          /* Order Information */
          <div className="space-y-6">
            {/* Order Header */}
            <div className="p-6 shadow-sm" style={{
              backgroundColor: theme.components.card.background,
              borderRadius: '16px',
              border: `1px solid ${theme.components.card.border}`,
            }}>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-2xl font-bold text-[var(--color-text-primary)]">
                    Order {formatOrderId(orderInfo.customOrderId || `#${orderInfo.orderId}`, DisplayContext.CUSTOMER)}
                  </h2>
                  <p className="text-[var(--color-text-secondary)]">
                    {orderInfo.vendorName}
                  </p>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold text-[var(--color-text-primary)]">
                    {formatCurrency(orderInfo.total)}
                  </div>
                  <div className="text-sm text-[var(--color-text-secondary)]">
                    {orderInfo.deliveryType.replace('_', ' ').toLowerCase()}
                  </div>
                </div>
              </div>

              {/* Status */}
              <div className="flex items-center justify-center p-4 rounded-2xl" style={{
                // The old wash was `getStatusColor(...) + '20'` — string-concatenating
                // an alpha-hex suffix onto the returned colour. That only worked while
                // the function returned a bare hex; now it returns a token, so the
                // tint has to be mixed, not concatenated.
                backgroundColor: `color-mix(in srgb, ${getStatusColor(orderInfo.status)} 12%, transparent)`,
                border: `1px solid color-mix(in srgb, ${getStatusColor(orderInfo.status)} 30%, transparent)`,
              }}>
                <span className="text-2xl mr-2">{getStatusIcon(orderInfo.status)}</span>
                <span className="text-xl font-semibold" style={{ color: getStatusColor(orderInfo.status) }}>
                  {orderInfo.status.replace('_', ' ')}
                </span>
              </div>
            </div>

            {/* Order Timeline */}
            <div className="p-6 shadow-sm" style={{
              backgroundColor: theme.components.card.background,
              borderRadius: '16px',
              border: `1px solid ${theme.components.card.border}`,
            }}>
              <h3 className="text-lg font-semibold text-[var(--color-text-primary)] mb-4">
                Order Timeline
              </h3>
              
              <div className="space-y-4">
                <div className="flex items-center">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold mr-4" style={{ backgroundColor: 'var(--status-warn)' }}>
                    1
                  </div>
                  <div className="flex-1">
                    <div className="font-medium text-[var(--color-text-primary)]">Order Placed</div>
                    <div className="text-sm text-[var(--color-text-secondary)]">
                      {formatDateTime(orderInfo.createdAt)}
                    </div>
                  </div>
                </div>

                {orderInfo.statusTimestamps.acceptedAt && (
                  <div className="flex items-center">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold mr-4" style={{ backgroundColor: 'var(--status-info)' }}>
                      2
                    </div>
                    <div className="flex-1">
                      <div className="font-medium text-[var(--color-text-primary)]">Order Accepted</div>
                      <div className="text-sm text-[var(--color-text-secondary)]">
                        {formatDateTime(orderInfo.statusTimestamps.acceptedAt)}
                      </div>
                    </div>
                  </div>
                )}

                {orderInfo.statusTimestamps.preparingAt && (
                  <div className="flex items-center">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold mr-4" style={{ backgroundColor: 'var(--status-warn)' }}>
                      3
                    </div>
                    <div className="flex-1">
                      <div className="font-medium text-[var(--color-text-primary)]">Preparing</div>
                      <div className="text-sm text-[var(--color-text-secondary)]">
                        {formatDateTime(orderInfo.statusTimestamps.preparingAt)}
                      </div>
                    </div>
                  </div>
                )}

                {orderInfo.statusTimestamps.readyAt && (
                  <div className="flex items-center">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold mr-4" style={{ backgroundColor: 'var(--status-ok)' }}>
                      4
                    </div>
                    <div className="flex-1">
                      <div className="font-medium text-[var(--color-text-primary)]">Ready for Pickup</div>
                      <div className="text-sm text-[var(--color-text-secondary)]">
                        {formatDateTime(orderInfo.statusTimestamps.readyAt)}
                      </div>
                    </div>
                  </div>
                )}

                {orderInfo.statusTimestamps.completedAt && (
                  <div className="flex items-center">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold mr-4" style={{ backgroundColor: 'var(--status-ok-text)' }}>
                      5
                    </div>
                    <div className="flex-1">
                      <div className="font-medium text-[var(--color-text-primary)]">Completed</div>
                      <div className="text-sm text-[var(--color-text-secondary)]">
                        {formatDateTime(orderInfo.statusTimestamps.completedAt)}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Order Items */}
            <div className="p-6 shadow-sm" style={{
              backgroundColor: theme.components.card.background,
              borderRadius: '16px',
              border: `1px solid ${theme.components.card.border}`,
            }}>
              <h3 className="text-lg font-semibold text-[var(--color-text-primary)] mb-4">
                Order Items
              </h3>
              
              <div className="space-y-3">
                {orderInfo.items.map((item, index) => (
                  <div key={index} className="flex items-center justify-between py-2 border-b border-[var(--color-border-default)] last:border-0">
                    {(item as any).imageUrl && (
                      <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0 mr-3">
                        <MenuItemImage imageUrl={(item as any).imageUrl} itemName={item.name} size="thumbnail" />
                      </div>
                    )}
                    <div className="flex-1">
                      <div className="font-medium text-[var(--color-text-primary)]">
                        {item.name}
                      </div>
                      <div className="text-sm text-[var(--color-text-secondary)]">
                        Quantity: {item.quantity}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-medium text-[var(--color-text-primary)]">
                        {formatCurrency(item.price * item.quantity)}
                      </div>
                      <div className="text-sm text-[var(--color-text-secondary)]">
                        {formatCurrency(item.price)} each
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-4 pt-4 border-t border-[var(--color-border-default)]">
                <div className="flex items-center justify-between">
                  <span className="text-lg font-semibold text-[var(--color-text-primary)]">
                    Total
                  </span>
                  <span className="text-xl font-bold text-[var(--color-text-primary)]">
                    {formatCurrency(orderInfo.total)}
                  </span>
                </div>
              </div>
            </div>

            {/* Customer Information */}
            <div className="p-6 shadow-sm" style={{
              backgroundColor: theme.components.card.background,
              borderRadius: '16px',
              border: `1px solid ${theme.components.card.border}`,
            }}>
              <h3 className="text-lg font-semibold text-[var(--color-text-primary)] mb-4">
                Customer Information
              </h3>
              
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-[var(--color-text-secondary)]">Name:</span>
                  <span className="text-[var(--color-text-primary)] font-medium">
                    {orderInfo.customerName}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[var(--color-text-secondary)]">Phone:</span>
                  <span className="text-[var(--color-text-primary)] font-medium">
                    {orderInfo.customerPhone}
                  </span>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-4">
              <button
                onClick={() => {
                  setOrderInfo(null);
                  setTrackingToken('');
                  setPhoneNumber('');
                  setError(null);
                }}
                className="flex-1 py-3 px-6 bg-[var(--color-background-default)] border border-[var(--color-border-default)] text-[var(--color-text-primary)] font-semibold rounded-lg transition-colors hover:bg-[var(--color-background-surface)]"
              >
                Track Another Order
              </button>
              
              <button
                onClick={() => navigate('/')}
                className="flex-1 py-3 px-6 hover:opacity-90 font-semibold rounded-xl transition-opacity"
                style={{
                  backgroundColor: theme.button.primary.solidBg || theme.button.primary.bg,
                  color: theme.button.primary.text,
                }}
              >
                Browse Menu
              </button>
            </div>
          </div>
        )}
      </div>
      </div>
      {/* Toaster provided globally via ToastProvider */}
    </>
  );
};

export default GuestOrderTracking; 